import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CalendarDays, Plus, Trash2, Users, Mail, MessageSquare, RefreshCw, ChevronDown, ChevronUp, Send, Download, Info, Video, BarChart3, PlayCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboardPage() {
  const [key, setKey] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [tab, setTab] = useState("ukazky");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Data
  const [dates, setDates] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [infoRequests, setInfoRequests] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [recEdits, setRecEdits] = useState({});
  const [recViews, setRecViews] = useState([]);
  const [savingRec, setSavingRec] = useState("");

  // Webinar add form
  const [showAdd, setShowAdd] = useState(false);
  const [newDate, setNewDate] = useState({ dateStr: "", time: "18:00", school_type: "zs" });

  // Detail
  const [expandedId, setExpandedId] = useState(null);
  const [detailRegs, setDetailRegs] = useState([]);
  const [detailLogs, setDetailLogs] = useState([]);
  const [sending, setSending] = useState("");
  const [reminderModal, setReminderModal] = useState(null);
  const [reminderEmail, setReminderEmail] = useState({ subject: "", body: "" });
  const [reminderSms, setReminderSms] = useState("");
  const [sendEmailFlag, setSendEmailFlag] = useState(true);
  const [sendSmsFlag, setSendSmsFlag] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [datesRes, regsRes, infoRes, recsRes, viewsRes] = await Promise.all([
        axios.get(`${API}/webinar-dates`),
        axios.get(`${API}/webinar-registrations?key=${key}`),
        axios.get(`${API}/info-requests?key=${key}`),
        axios.get(`${API}/recordings-admin?key=${key}`),
        axios.get(`${API}/recording-views?key=${key}`)
      ]);
      if (Array.isArray(datesRes.data)) setDates(datesRes.data);
      if (!regsRes.data.error) setRegistrations(regsRes.data);
      if (!infoRes.data.error) setInfoRequests(infoRes.data);
      if (Array.isArray(recsRes.data)) {
        setRecordings(recsRes.data);
        const m = {};
        recsRes.data.forEach(r => { m[r.slot] = { title: r.title || "", embed_url: r.embed_url || "", release_date: r.release_date || "" }; });
        setRecEdits(m);
      }
      if (Array.isArray(viewsRes.data)) setRecViews(viewsRes.data);
      setAuthorized(true);
    } catch (e) { setError("Nepodařilo se načíst data."); }
    finally { setLoading(false); }
  };

  const handleAuth = (e) => { e.preventDefault(); fetchAll(); };

  const extractEmbedUrl = (raw) => {
    const m = (raw || "").match(/src=["']([^"']+)["']/i);
    return m ? m[1] : (raw || "").trim();
  };

  const saveRecording = async (slot) => {
    const e = recEdits[slot];
    if (!e) return;
    setSavingRec(slot);
    try {
      const embed_url = extractEmbedUrl(e.embed_url);
      await axios.post(`${API}/recordings-admin/update`, { admin_key: key, slot, title: e.title, embed_url, release_date: e.release_date });
      setRecEdits({ ...recEdits, [slot]: { ...e, embed_url } });
      setRecordings(recordings.map(r => r.slot === slot ? { ...r, title: e.title, embed_url, release_date: e.release_date } : r));
    } catch (err) { alert("Chyba při ukládání záznamu."); }
    finally { setSavingRec(""); }
  };

  const addDate = async () => {
    if (!newDate.dateStr) return;
    const dateStr = `${newDate.dateStr}T${newDate.time}:00`;
    const d = new Date(dateStr);
    const days = ["Ne","Po","Út","St","Čt","Pá","So"];
    const dayLabel = `${days[d.getDay()]} ${d.getDate()}. ${d.getMonth()+1}.`;
    const timeLabel = `${newDate.time}–${String(parseInt(newDate.time)+1).padStart(2,'0')}:00`;
    await axios.post(`${API}/webinar-dates`, { admin_key: key, date: dateStr, timeLabel, dayLabel, school_type: newDate.school_type });
    setShowAdd(false);
    setNewDate({ dateStr: "", time: "18:00", school_type: "zs" });
    fetchAll();
  };

  const deleteDate = async (id) => {
    if (!window.confirm("Opravdu smazat tento termín?")) return;
    await axios.delete(`${API}/webinar-dates/${id}?key=${key}`);
    fetchAll();
  };

  const loadDetail = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    const [regsRes, logsRes] = await Promise.all([
      axios.get(`${API}/webinar-dates/${id}/registrations?key=${key}`),
      axios.get(`${API}/reminder-logs?key=${key}&date_id=${id}`)
    ]);
    setDetailRegs(regsRes.data.registrations || []);
    setDetailLogs(Array.isArray(logsRes.data) ? logsRes.data : []);
    setExpandedId(id);
  };

  const openReminderModal = (dateId, regId, d) => {
    const sl = d.school_type === "zs" ? "ZŠ" : "MŠ";
    const dd = `${d.dayLabel} ${d.timeLabel}`;
    const meet = "https://meet.google.com/paq-hdpq-ebi";
    setReminderEmail({
      subject: `Připomínka: Živá ukázka Agent ŠVP (${sl}) — ${dd}`,
      body: `Dobrý den,\n\npřipomínáme Vám dnešní živou ukázku Agent ŠVP pro ${sl}.\n\nTermín: ${dd}\n\nPřipojte se zde: ${meet}\n\nTěšíme se na Vás!\nTým Agent ŠVP`
    });
    setReminderSms(`Pripominame Vam dnesni zivou ukazku Agent SVP (${sl}) - ${dd}. Pripojte se: ${meet}`);
    setSendEmailFlag(true);
    setSendSmsFlag(true);
    setReminderModal({ dateId, regId: regId || "", d });
  };

  const submitReminder = async () => {
    if (!reminderModal) return;
    setSending(reminderModal.regId || reminderModal.dateId);
    try {
      const res = await axios.post(`${API}/send-reminder`, {
        admin_key: key, date_id: reminderModal.dateId, registration_id: reminderModal.regId,
        send_email: sendEmailFlag, send_sms: sendSmsFlag,
        custom_email_subject: reminderEmail.subject, custom_email_body: reminderEmail.body, custom_sms_text: reminderSms
      });
      const logId = res.data.log_id;
      setReminderModal(null);
      
      // Poll for status
      alert(`Odesílání ${res.data.total} připomínek zahájeno na pozadí. Výsledek uvidíte v logu po rozkliknutí termínu.`);
      
      // Auto-refresh detail after a delay
      if (expandedId) {
        setTimeout(() => loadDetail(expandedId), 5000);
        setTimeout(() => loadDetail(expandedId), 15000);
        setTimeout(() => loadDetail(expandedId), 30000);
      }
    } catch (e) { alert("Chyba při zahájení odesílání."); }
    finally { setSending(""); }
  };

  const formatDate = (d) => { try { return new Date(d).toLocaleDateString("cs-CZ", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" }); } catch { return d; } };
  const formatWebDate = (d) => { try { const dt = new Date(d); return dt.toLocaleDateString("cs-CZ", { weekday:"short", day:"2-digit", month:"2-digit", year:"numeric" }) + " " + dt.toLocaleTimeString("cs-CZ", { hour:"2-digit", minute:"2-digit" }); } catch { return d; } };
  const parseLocal = (dateStr) => {
    if (!dateStr) return new Date(0);
    const clean = dateStr.replace("Z", "").replace(/[+-]\d{2}:\d{2}$/, "");
    const [dp, tp] = clean.split("T");
    const [y, m, d] = dp.split("-").map(Number);
    const [h, mi, s] = (tp || "00:00:00").split(":").map(Number);
    return new Date(y, m - 1, d, h, mi, s || 0);
  };
  const isPast = (d) => parseLocal(d) < new Date();

  const exportCSV = (data, filename, headers, rowFn) => {
    const rows = data.map(rowFn);
    const csv = [headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  };

  // Count registrations per date
  const getRegCount = (dateStr) => registrations.filter(r => r.webinar_date === dateStr).length;

  if (!authorized) {
    return (
      <div className="min-h-screen bg-[#F4F7FC] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl border border-[#E8EDF5] p-8 max-w-sm w-full">
          <BarChart3 className="w-10 h-10 text-[#0049B3] mx-auto mb-4" />
          <h1 className="text-xl font-bold text-center mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Admin Agent ŠVP</h1>
          <p className="text-sm text-[#6B7A99] text-center mb-6">Správa ukázek, registrací a kontaktů</p>
          <form onSubmit={handleAuth} className="space-y-4">
            <Input type="password" placeholder="Přístupový klíč" value={key} onChange={(e) => setKey(e.target.value)} className="rounded-xl py-5" />
            {error && <p className="text-sm text-red-500">{error}</p>}
            <Button type="submit" className="w-full bg-[#0049B3] text-white rounded-xl py-5 font-bold">{loading ? "Načítám..." : "Přihlásit"}</Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7FC]">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-[#0F1B3D]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Admin Agent ŠVP</h1>
          <Button variant="outline" className="rounded-xl" onClick={fetchAll} disabled={loading}><RefreshCw className={`w-4 h-4 mr-2 ${loading?"animate-spin":""}`} />Obnovit</Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-[#E8EDF5] p-4 text-center">
            <div className="text-2xl font-extrabold text-[#0049B3]">{dates.filter(d => !isPast(d.date)).length}</div>
            <div className="text-xs text-[#6B7A99] mt-1">Nadcházející ukázky</div>
          </div>
          <div className="bg-white rounded-xl border border-[#E8EDF5] p-4 text-center">
            <div className="text-2xl font-extrabold text-[#FFC72C]">{registrations.length}</div>
            <div className="text-xs text-[#6B7A99] mt-1">Registrace na ukázky</div>
          </div>
          <div className="bg-white rounded-xl border border-[#E8EDF5] p-4 text-center">
            <div className="text-2xl font-extrabold text-emerald-600">{infoRequests.length}</div>
            <div className="text-xs text-[#6B7A99] mt-1">Více informací</div>
          </div>
          <div className="bg-white rounded-xl border border-[#E8EDF5] p-4 text-center">
            <div className="text-2xl font-extrabold text-purple-600">{registrations.filter(r => r.operator_id).length}</div>
            <div className="text-xs text-[#6B7A99] mt-1">Přes operátory</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: "ukazky", icon: CalendarDays, label: "Živé ukázky" },
            { id: "registrace", icon: Video, label: `Registrace (${registrations.length})` },
            { id: "info", icon: Info, label: `Více informací (${infoRequests.length})` },
            { id: "zaznamy", icon: PlayCircle, label: `Záznamy ukázek (${recViews.length})` }
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all ${tab === t.id ? "bg-[#0049B3] text-white shadow-lg" : "bg-white text-[#3D4F6F] border border-[#E8EDF5] hover:bg-[#F8FAFF]"}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>

        {/* ===== ŽIVÉ UKÁZKY TAB ===== */}
        {tab === "ukazky" && (
          <>
            <div className="flex justify-end mb-4">
              <Button className="bg-[#0049B3] text-white rounded-xl" onClick={() => setShowAdd(!showAdd)}><Plus className="w-4 h-4 mr-2" />Přidat termín</Button>
            </div>
            {showAdd && (
              <div className="bg-white rounded-2xl border border-[#E8EDF5] p-6 mb-6 flex flex-wrap gap-4 items-end">
                <div className="space-y-1"><label className="text-xs font-bold text-[#6B7A99]">Datum</label><Input type="date" value={newDate.dateStr} onChange={(e) => setNewDate({...newDate, dateStr: e.target.value})} className="rounded-xl" /></div>
                <div className="space-y-1"><label className="text-xs font-bold text-[#6B7A99]">Čas</label>
                  <Input type="time" value={newDate.time} onChange={(e) => setNewDate({...newDate, time: e.target.value})} className="rounded-xl w-32" />
                </div>
                <div className="space-y-1"><label className="text-xs font-bold text-[#6B7A99]">Typ</label>
                  <Select value={newDate.school_type} onValueChange={(v) => setNewDate({...newDate, school_type: v})}><SelectTrigger className="rounded-xl w-24"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="zs">ZŠ</SelectItem><SelectItem value="ms">MŠ</SelectItem></SelectContent>
                  </Select>
                </div>
                <Button className="bg-emerald-500 text-white rounded-xl" onClick={addDate}>Vytvořit</Button>
              </div>
            )}
            <div className="space-y-3">
              {dates.map(d => {
                const past = isPast(d.date);
                const expanded = expandedId === d.id;
                const regCount = getRegCount(d.date);
                return (
                  <div key={d.id} className={`bg-white rounded-2xl border ${past ? "border-gray-200 opacity-70" : "border-[#E8EDF5]"} overflow-hidden`}>
                    <div className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-[#F8FAFF] transition-colors" onClick={() => loadDetail(d.id)}>
                      <div className="flex items-center gap-4">
                        <CalendarDays className={`w-5 h-5 ${past ? "text-gray-400" : "text-[#0049B3]"}`} />
                        <span className="font-bold text-[#0F1B3D]">{d.dayLabel}</span>
                        <span className="text-[#4A5B7A]">{d.timeLabel}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${d.school_type==="zs"?"bg-amber-100 text-amber-700":"bg-blue-100 text-blue-700"}`}>{d.school_type==="zs"?"ZŠ":"MŠ"}</span>
                        <span className="text-xs bg-[#F4F7FC] text-[#6B7A99] px-2 py-0.5 rounded-full"><Users className="w-3 h-3 inline mr-1" />{regCount}</span>
                        {past && <span className="text-xs text-gray-400">Proběhla</span>}
                      </div>
                      <div className="flex items-center gap-3">
                        {!past && (
                          <>
                            <Button size="sm" variant="outline" className="text-xs rounded-lg gap-1" onClick={(e) => { e.stopPropagation(); openReminderModal(d.id, "", d); }} disabled={!!sending}>
                              <Send className="w-3 h-3" />{sending===d.id ? "..." : "Připomenout"}
                            </Button>
                            <Button size="sm" variant="outline" className="text-xs rounded-lg text-red-500" onClick={(e) => { e.stopPropagation(); deleteDate(d.id); }}><Trash2 className="w-3 h-3" /></Button>
                          </>
                        )}
                        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </div>
                    {expanded && (
                      <div className="border-t border-[#E8EDF5] px-5 py-4 bg-[#F8FAFF]">
                        <h4 className="text-sm font-bold text-[#6B7A99] mb-3">Přihlášení ({detailRegs.length})</h4>
                        {detailRegs.length === 0 ? <p className="text-sm text-[#9CA3AF]">Zatím nikdo.</p> : (
                          <div className="space-y-2">
                            {detailRegs.map((r, i) => (
                              <div key={r.id||i} className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-[#E8EDF5]">
                                <div className="flex items-center gap-3 flex-wrap">
                                  <span className="text-sm font-semibold text-[#0F1B3D]">{r.name}</span>
                                  <a href={`mailto:${r.email}`} className="text-xs text-[#0049B3]">{r.email}</a>
                                  {r.phone && <a href={`tel:${r.phone}`} className="text-xs text-[#0049B3]">{r.phone}</a>}
                                  {r.school_name && <span className="text-xs text-[#6B7A99]">{r.school_name}</span>}
                                  {r.ico && <span className="text-xs text-[#6B7A99]">IČO:{r.ico}</span>}
                                  {r.operator_id && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Op.#{r.operator_id}</span>}
                                  {r.source === "operator" && <span className="text-xs bg-purple-50 text-purple-500 px-1.5 py-0.5 rounded">operátor</span>}
                                </div>
                                {!past && (
                                  <div className="flex gap-1">
                                    <button title="Email" onClick={() => openReminderModal(d.id, r.id, d)} className="p-1.5 rounded-lg hover:bg-blue-50"><Mail className="w-3.5 h-3.5 text-[#0049B3]" /></button>
                                    <button title="SMS" onClick={() => openReminderModal(d.id, r.id, d)} className="p-1.5 rounded-lg hover:bg-emerald-50"><MessageSquare className="w-3.5 h-3.5 text-emerald-600" /></button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Logy odeslání */}
                        {detailLogs.length > 0 && (
                          <div className="mt-6 pt-4 border-t border-[#E8EDF5]">
                            <h4 className="text-sm font-bold text-[#6B7A99] mb-3">Historie odeslaných připomínek</h4>
                            <div className="space-y-3">
                              {detailLogs.map((log, li) => (
                                <div key={li} className="bg-white rounded-xl border border-[#E8EDF5] p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                      <span className="text-xs text-[#9CA3AF]">{formatDate(log.created_at)}</span>
                                      {log.status === "sending" ? (
                                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full animate-pulse">Odesílá se...</span>
                                      ) : (
                                        <>
                                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{log.sent_email} emailů</span>
                                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{log.sent_sms} SMS</span>
                                          <span className="text-xs text-[#6B7A99]">/ {log.total} účastníků</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                                    {(log.logs || []).map((entry, ei) => (
                                      <div key={ei} className="flex items-center gap-2 text-xs">
                                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${entry.status === "ok" ? "bg-emerald-500" : "bg-red-500"}`} />
                                        <span className="font-medium text-[#3D4F6F] w-12">{entry.type === "email" ? "Email" : "SMS"}</span>
                                        <span className="text-[#0049B3]">{entry.to}</span>
                                        <span className="text-[#6B7A99]">{entry.name}</span>
                                        {entry.status === "fail" && <span className="text-red-500">CHYBA{entry.error ? `: ${entry.error.substring(0, 50)}` : ""}</span>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ===== REGISTRACE TAB ===== */}
        {tab === "registrace" && (
          <>
            <div className="flex justify-end mb-4">
              <Button className="bg-[#0049B3] text-white rounded-xl" onClick={() => exportCSV(registrations, `registrace-${new Date().toISOString().slice(0,10)}.csv`,
                ["Jméno","Email","Telefon","Škola/IČO","Typ","Termín","Operátor","Zdroj","Datum"],
                r => [r.name,r.email,r.phone||"",r.school_name||r.ico||"",r.school_type||"",formatWebDate(r.webinar_date),r.operator_id||"",r.source||"web",formatDate(r.registered_at)]
              )}><Download className="w-4 h-4 mr-2" />Export CSV</Button>
            </div>
            <div className="bg-white rounded-2xl border border-[#E8EDF5] shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-[#E8EDF5] bg-[#F8FAFF]">
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">#</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Jméno</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Telefon</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Škola/IČO</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Typ</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Termín</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Op.</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Datum</th>
                  </tr></thead>
                  <tbody>
                    {registrations.length === 0 ? <tr><td colSpan={9} className="text-center py-12 text-[#9CA3AF]">Zatím žádné registrace</td></tr> :
                    registrations.map((r, idx) => (
                      <tr key={r.id||idx} className="border-b border-[#F0F2F5] hover:bg-[#F8FAFF]">
                        <td className="px-4 py-3 text-sm text-[#9CA3AF]">{idx+1}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-[#0F1B3D]">{r.name}</td>
                        <td className="px-4 py-3 text-sm"><a href={`mailto:${r.email}`} className="text-[#0049B3] hover:underline">{r.email}</a></td>
                        <td className="px-4 py-3 text-sm">{r.phone ? <a href={`tel:${r.phone}`} className="text-[#0049B3]">{r.phone}</a> : "—"}</td>
                        <td className="px-4 py-3 text-sm text-[#3D4F6F]">{r.school_name || r.ico || "—"}</td>
                        <td className="px-4 py-3">{r.school_type ? <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.school_type==="zs"?"bg-amber-100 text-amber-700":"bg-blue-100 text-blue-700"}`}>{r.school_type==="zs"?"ZŠ":"MŠ"}</span> : "—"}</td>
                        <td className="px-4 py-3 text-sm text-[#3D4F6F]">{formatWebDate(r.webinar_date)}</td>
                        <td className="px-4 py-3 text-sm">{r.operator_id ? <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs">#{r.operator_id}</span> : "—"}</td>
                        <td className="px-4 py-3 text-xs text-[#9CA3AF]">{formatDate(r.registered_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ===== INFO TAB ===== */}
        {tab === "info" && (
          <>
            <div className="flex justify-end mb-4">
              <Button className="bg-[#0049B3] text-white rounded-xl" onClick={() => exportCSV(infoRequests, `info-${new Date().toISOString().slice(0,10)}.csv`,
                ["Jméno","Email","Telefon","Škola","Zpráva","Datum"],
                r => [r.name,r.email,r.phone||"",r.school_name||"",r.message||"",formatDate(r.registered_at)]
              )}><Download className="w-4 h-4 mr-2" />Export CSV</Button>
            </div>
            <div className="bg-white rounded-2xl border border-[#E8EDF5] shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-[#E8EDF5] bg-[#F8FAFF]">
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">#</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Jméno</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Telefon</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Škola</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Zpráva</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Datum</th>
                  </tr></thead>
                  <tbody>
                    {infoRequests.length === 0 ? <tr><td colSpan={7} className="text-center py-12 text-[#9CA3AF]">Zatím žádné</td></tr> :
                    infoRequests.map((r, idx) => (
                      <tr key={r.id||idx} className="border-b border-[#F0F2F5] hover:bg-[#F8FAFF]">
                        <td className="px-4 py-3 text-sm text-[#9CA3AF]">{idx+1}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-[#0F1B3D]">{r.name}</td>
                        <td className="px-4 py-3 text-sm"><a href={`mailto:${r.email}`} className="text-[#0049B3] hover:underline">{r.email}</a></td>
                        <td className="px-4 py-3 text-sm">{r.phone ? <a href={`tel:${r.phone}`} className="text-[#0049B3]">{r.phone}</a> : "—"}</td>
                        <td className="px-4 py-3 text-sm text-[#3D4F6F]">{r.school_name || "—"}</td>
                        <td className="px-4 py-3 text-sm text-[#3D4F6F] max-w-md whitespace-pre-wrap break-words">{r.message || "—"}</td>
                        <td className="px-4 py-3 text-xs text-[#9CA3AF]">{formatDate(r.registered_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ===== ZÁZNAMY UKÁZEK TAB ===== */}
        {tab === "zaznamy" && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
              {[["ms", "Mateřské školy"], ["zs", "Základní školy"]].map(([st, label]) => (
                <div key={st} className="space-y-4">
                  <h3 className="text-sm font-bold text-[#6B7A99] uppercase tracking-wide">{label}</h3>
                  {recordings.filter(r => r.school_type === st).map((r, i) => {
                    const edit = recEdits[r.slot] || { title: "", embed_url: "", release_date: "" };
                    const previewUrl = extractEmbedUrl(edit.embed_url);
                    return (
                      <div key={r.slot} data-testid={`admin-recording-card-${r.slot}`} className="bg-white rounded-2xl border border-[#E8EDF5] p-5 space-y-3">
                        <p className="text-xs font-bold text-[#9CA3AF] uppercase">Slot {i + 1}</p>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-[#6B7A99]">Název záznamu</label>
                          <Input data-testid={`admin-recording-title-${r.slot}`} value={edit.title} onChange={(e) => setRecEdits({ ...recEdits, [r.slot]: { ...edit, title: e.target.value } })} placeholder={`Záznam ukázky ${st.toUpperCase()}`} className="rounded-xl text-sm" />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-[#6B7A99]">Embed kód videa (celý iframe nebo jen URL)</label>
                          <textarea data-testid={`admin-recording-embed-${r.slot}`} value={edit.embed_url} onChange={(e) => setRecEdits({ ...recEdits, [r.slot]: { ...edit, embed_url: e.target.value } })} placeholder='<iframe src="https://..." ...></iframe>' className="w-full rounded-xl border border-[#E8EDF5] p-3 text-xs font-mono min-h-[70px] focus:outline-none focus:ring-2 focus:ring-[#0049B3]/20 focus:border-[#0049B3]" />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-[#6B7A99]">Datum a čas vydání</label>
                          <Input data-testid={`admin-recording-date-${r.slot}`} type="datetime-local" value={edit.release_date} onChange={(e) => setRecEdits({ ...recEdits, [r.slot]: { ...edit, release_date: e.target.value } })} className="rounded-xl text-sm" />
                        </div>
                        {/^https?:\/\//i.test(previewUrl) && (
                          <div className="aspect-video rounded-xl overflow-hidden bg-black">
                            <iframe src={previewUrl} title={r.slot} className="w-full h-full" frameBorder="0" allowFullScreen />
                          </div>
                        )}
                        <Button data-testid={`admin-recording-save-${r.slot}`} onClick={() => saveRecording(r.slot)} disabled={savingRec === r.slot} className="w-full bg-[#0049B3] text-white rounded-xl font-bold">
                          {savingRec === r.slot ? "Ukládám..." : "Uložit záznam"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Views log */}
            <div className="bg-white rounded-2xl border border-[#E8EDF5] shadow-sm overflow-hidden" data-testid="recording-views-log">
              <div className="px-5 py-4 border-b border-[#E8EDF5] bg-[#F8FAFF]">
                <h3 className="text-sm font-bold text-[#0F1B3D]">Kdo si prohlédl záznamy ({recViews.length})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-[#E8EDF5]">
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">#</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Jméno</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Identifikátor</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Škola</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Sekce</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase">Kdy</th>
                  </tr></thead>
                  <tbody>
                    {recViews.length === 0 ? <tr><td colSpan={6} className="text-center py-12 text-[#9CA3AF]">Zatím žádná zhlédnutí</td></tr> :
                    recViews.map((v, idx) => (
                      <tr key={v.id || idx} className="border-b border-[#F0F2F5] hover:bg-[#F8FAFF]">
                        <td className="px-4 py-3 text-sm text-[#9CA3AF]">{idx + 1}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-[#0F1B3D]">{v.name || "—"}</td>
                        <td className="px-4 py-3 text-sm text-[#0049B3]">{v.identifier}</td>
                        <td className="px-4 py-3 text-sm text-[#3D4F6F]">{v.school_name || "—"}</td>
                        <td className="px-4 py-3"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${v.school_type === "zs" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{v.school_type === "zs" ? "ZŠ" : "MŠ"}</span></td>
                        <td className="px-4 py-3 text-xs text-[#9CA3AF]">{formatDate(v.viewed_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Reminder Modal */}
      {reminderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-[#E8EDF5] max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-[#0049B3] to-[#003380] p-5 rounded-t-2xl">
              <h3 className="text-lg font-bold text-white">Odeslat připomínku</h3>
              <p className="text-blue-200/60 text-sm">{reminderModal.regId ? "Jednomu účastníkovi" : "Všem přihlášeným"}</p>
            </div>
            <div className="p-6 space-y-5">
              {/* Checkboxes */}
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={sendEmailFlag} onChange={(e) => setSendEmailFlag(e.target.checked)} className="w-4 h-4 rounded" />
                  <Mail className="w-4 h-4 text-[#0049B3]" /><span className="text-sm font-semibold">Email</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={sendSmsFlag} onChange={(e) => setSendSmsFlag(e.target.checked)} className="w-4 h-4 rounded" />
                  <MessageSquare className="w-4 h-4 text-emerald-600" /><span className="text-sm font-semibold">SMS</span>
                </label>
              </div>

              {/* Email */}
              {sendEmailFlag && (
                <div className="space-y-3 bg-[#F8FAFF] rounded-xl p-4 border border-[#E8EDF5]">
                  <h4 className="text-xs font-bold text-[#6B7A99] uppercase">Email připomínka</h4>
                  <div>
                    <label className="text-xs font-semibold text-[#3D4F6F]">Předmět</label>
                    <Input value={reminderEmail.subject} onChange={(e) => setReminderEmail({...reminderEmail, subject: e.target.value})} className="mt-1 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#3D4F6F]">Text emailu</label>
                    <textarea value={reminderEmail.body} onChange={(e) => setReminderEmail({...reminderEmail, body: e.target.value})} className="mt-1 w-full rounded-lg border border-[#E8EDF5] p-3 text-sm min-h-[150px] focus:outline-none focus:ring-2 focus:ring-[#0049B3]/20 focus:border-[#0049B3]" />
                  </div>
                </div>
              )}

              {/* SMS */}
              {sendSmsFlag && (
                <div className="space-y-3 bg-emerald-50/50 rounded-xl p-4 border border-emerald-100">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-[#6B7A99] uppercase">SMS připomínka</h4>
                    <span className="text-xs text-[#9CA3AF]">{reminderSms.length} znaků</span>
                  </div>
                  <textarea value={reminderSms} onChange={(e) => setReminderSms(e.target.value)} className="w-full rounded-lg border border-emerald-200 p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400" />
                  <p className="text-xs text-[#9CA3AF]">Max 160 znaků = 1 SMS. Bez diakritiky pro lepší doručitelnost.</p>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <Button className="flex-1 bg-[#0049B3] hover:bg-[#003380] text-white rounded-xl font-bold py-5" onClick={submitReminder} disabled={!!sending}>
                  {sending ? "Odesílám..." : "Odeslat připomínku"}
                </Button>
                <Button variant="outline" className="rounded-xl px-6 py-5" onClick={() => setReminderModal(null)}>Zrušit</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
